import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { q } from "./api.ts";
import { INBOX_LABELS as I, WAKEUP_LABELS as L } from "./labels.ts";
import { ErrorText, Section } from "./ui.tsx";
import { PropertyList, PropertyRow } from "./ui/props.tsx";
import { Skeleton } from "./ui/skeleton.tsx";
import { StatusIcon } from "./ui/status.tsx";

// Scheduled wake-ups (design/architecture.md §2.5, decided 2026-11-03;
// docs/design-system.md §0.4c and §0.4g). zooi starts no agent: a schedule
// the person set up checks in when it asks what is due, and these lines say
// whether one has, so a product nobody runs on their own is never mistaken
// for one that is. The state comes from the week view, never from the page.

/** The plugin's guide to the schedules, in the public plugin repository. */
export const SCHEDULING_GUIDE = "https://github.com/ZooiAI/zooi-plugin/blob/main/SCHEDULING.md";
const DAY = 86_400_000;

type WakeupState = "none" | "active" | "gone";
const ICON = { none: "not_started", active: "done", gone: "stopped" } as const;
const stateOf = (wakeup: C.Wakeup | null): WakeupState => (!wakeup ? "none" : wakeup.current ? "active" : "gone");
const daysSince = (iso: string) => Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / DAY));

/** What the schedule is, from what it declared: "Claude Code routine", "Your computer's scheduler, starting Codex". */
export function wakeupName(wakeup: C.Wakeup): string {
  const engine = wakeup.engine ? I.engine[wakeup.engine] ?? wakeup.engine : null;
  if (wakeup.kind === "scheduler") return engine ? L.schedulerOf(engine) : L.scheduler;
  if (wakeup.kind === "dispatch") return engine ? L.dispatchOf(engine) : L.dispatch;
  return engine ? L.routineOf(engine) : L.routine;
}

const whenOf = (wakeup: C.Wakeup) => (wakeup.current ? L.lastSeen(wakeup.last_seen_at) : L.stale(daysSince(wakeup.last_seen_at)));

/** One schedule as a property row: the status icon, its name, and when it last checked in. */
function WakeupRow({ wakeup }: { wakeup: C.Wakeup }) {
  return <PropertyRow icon={<StatusIcon kind={ICON[stateOf(wakeup)]} label="" />} label={wakeupName(wakeup)}>{whenOf(wakeup)}</PropertyRow>;
}

/** The week page's line (§0.4c): the latest schedule seen, or that there is none. */
export function WakeupLine({ wakeups }: { wakeups: C.Wakeup[] }) {
  const latest = wakeups[0] ?? null;
  return <PropertyRow icon={<StatusIcon kind={ICON[stateOf(latest)]} label="" />} label={L.title}>
    {latest ? `${wakeupName(latest)} · ${whenOf(latest)}` : L.none}
  </PropertyRow>;
}

/** Under that line, while nobody runs the product on their own: what that means and where to set a schedule up. */
export function WakeupNote({ wakeups, productId }: { wakeups: C.Wakeup[]; productId: string }) {
  const state = stateOf(wakeups[0] ?? null);
  if (state === "active") return null;
  return <p className="z-small z-muted">{state === "none" ? L.noneNote : L.staleNote} <Link className="z-link" to="/settings/products/$productId/agent" params={{ productId }} hash="wakeup">{L.guide}</Link></p>;
}

/** Settings › Agent and runs (§0.4g): every schedule seen for the product, and how to set one up. */
export function WakeupSettings({ product }: { product: C.Product }) {
  const week = useQuery(q.week(product.id));
  return <Section id="wakeup" title={L.title} note={L.note}>
    {week.error ? <ErrorText error={week.error} />
      : !week.data ? <Skeleton shape="list" rows={1} />
      : week.data.wakeups.length ? <PropertyList>{week.data.wakeups.map((wakeup) => <WakeupRow key={`${wakeup.kind}:${wakeup.engine ?? ""}`} wakeup={wakeup} />)}</PropertyList>
      : <p className="z-muted"><StatusIcon kind="not_started" label="" /> {L.none} · {L.noneNote}</p>}
    <p className="z-small"><a className="z-link" href={SCHEDULING_GUIDE} target="_blank" rel="noreferrer">{L.guide}</a></p>
  </Section>;
}
